import PropTypes from 'prop-types';
import { makeAutoObservable } from 'mobx';
import { createContext, useContext } from 'react';
import { tacticMap } from '../../classes/tactics/tacticKeys';
import { removeTradePlanByIds } from './utils/outgoingMessages/tradePlan/removetradeplan/removeTradePlansById';
import { groupByFunction } from './utils/rxjs/groupByFunction';
import { AlertsNotificationStore } from '../alertsNotificationStore/alertsNotificationStore';

export class AlertsStore {
  root;
  notificationStore;
  alertTradePlansById = {};
  alertTradePlansByWatchlistName = {};
  savedAlertTradePlanConfigurations = {};
  alertSortingStore = {};
  dataSource = 'LIVE';

  constructor(root) {
    makeAutoObservable(this);
    this.root = root;
    this.notificationStore = new AlertsNotificationStore(root);
    this.initializeStreams();
  }

  initializeStreams = () => {
    const alertsStream = groupByFunction(
      'messageType',
      'tradePlanAlerts',
      this.root.tradePlanWebsocketSubject
    );
    alertsStream.subscribe(this.handleAlertTradePlanStream);

    const removeAlertStream = groupByFunction(
      'messageType',
      'removeAlertTradePlan',
      this.root.tradePlanWebsocketSubject
    );
    removeAlertStream.subscribe(this.handleRemoveAlertTradePlanStream);

    const softOrderStream = groupByFunction(
      'messageType',
      'softOrder',
      this.root.websocketSubject
    );
    groupByFunction('tradePlanType', 'ALERT', softOrderStream).subscribe(
      this.handleAlertSoftOrderStream
    );

    const alertTradePlansConfigurationsStream = groupByFunction(
      'messageType',
      'alertTradePlansConfigurations',
      this.root.websocketSubject
    );
    alertTradePlansConfigurationsStream.subscribe(
      this.handleSavedAlertTradePlanConfigurationStream
    );

    const watchlistNamesStream = groupByFunction(
      'messageType',
      'watchlistNames',
      this.root.websocketSubject
    );
    watchlistNamesStream.subscribe((msg) =>
      this.handleWatchlistNamesStream(msg)
    );
  };

  handleAlertTradePlanStream = (msg) => {
    const tradePlans = msg.tradePlanAlerts;
    if (tradePlans) {
      for (const id in tradePlans) {
        this.alertTradePlansById[id] = tradePlans[id];
        const watchlistName = tradePlans[id].watchlistName;
        if (this.alertTradePlansByWatchlistName[watchlistName]) {
          this.alertTradePlansByWatchlistName[watchlistName][id] =
            tradePlans[id];
        } else {
          this.alertTradePlansByWatchlistName[watchlistName] = {
            [id]: tradePlans[id],
          };
        }
        if (!this.alertSortingStore[id]) {
          this.alertSortingStore[id] = {
            score: 100,
            triggerPrice: false,
            setupDetails: null,
            filters: {},
          };
        }
      }
    }
  };

  handleRemoveAlertTradePlanStream = (msg) => {
    const id = msg.alertId;
    if (this.alertTradePlansById[id]) {
      const watchlistName = this.alertTradePlansById[id].watchlistName;
      delete this.alertTradePlansById[id];
      delete this.alertTradePlansByWatchlistName[watchlistName][id];
      if (this.alertSortingStore[id]) delete this.alertSortingStore[id];
    }
  };
  handleSavedAlertTradePlanConfigurationStream = (msg) => {
    const obj = {};
    msg.configurations.forEach(
      (config) => (obj[config.configurationName] = config)
    );
    this.savedAlertTradePlanConfigurations = obj;
  };

  handleAlertSoftOrderStream = (msg) => {
    const alertId = msg.tradePlanId;
    const tactic = msg.value.tactic;
    const category = tacticMap[tactic].category;
    if (category === 'Setup') {
      const detailParser = tacticMap[tactic].setupDetailParser;
      let setupDetails = detailParser(this.root, msg);
      if (tactic === 'CANDLE_PATTERN') setupDetails = setupDetails[0];
      if (this.alertSortingStore[alertId]) {
        this.alertSortingStore[alertId].setupDetails = setupDetails.details;
        this.alertSortingStore[alertId].score = msg.value.tacticValue.score;
        this.alertSortingStore[alertId].triggerPrice =
          setupDetails.triggerPrice;
      } else {
        this.alertSortingStore[alertId] = {
          score: msg.value.tacticValue.score,
          triggerPrice: setupDetails.triggerPrice,
          setupDetails: setupDetails.details,
          filters: {},
        };
      }
    }
    if (category === 'Setup Filter') {
      const filterStatus = tacticMap[tactic].filterStatusParser(this.root, msg);
      const filter = msg.value.tactic;
      if (this.alertSortingStore[alertId]) {
        this.alertSortingStore[alertId].filters[msg.value.tactic] =
          filterStatus;
      } else {
        this.alertSortingStore[alertId] = {
          score: 100,
          triggerPrice: false,
          setupDetails: null,
          filters: {
            [filter]: filterStatus,
          },
        };
      }
      //check if all filters are either true or false and change the filteredOut value
    }
  };
  handleWatchlistNamesStream = (msg) => {
    const names = msg?.names ?? [];
    names.forEach((name) => {
      if (!this.alertTradePlansByWatchlistName[name])
        this.alertTradePlansByWatchlistName[name] = {};
    });
  };
  // getters
  get listOfWatchlistNames() {
    return Object.keys(this.alertTradePlansByWatchlistName);
  }
  get savedConfigurationList() {
    return Object.keys(this.savedAlertTradePlanConfigurations);
  }

  //functions
  addNewWatchlist = (watchlistName) => {
    if (!this.alertTradePlansByWatchlistName[watchlistName])
      this.alertTradePlansByWatchlistName[watchlistName] = {};
  };
  clearWatchlist = (watchlistName) => {
    const alertIdArray = Object.keys(
      this.alertTradePlansByWatchlistName[watchlistName]
    ).filter((id) => id !== 'groupNumber');
    removeTradePlanByIds(alertIdArray);
  };

  removeWatchlist = (watchlistName) => {
    const alertIdArray = Object.keys(
      this.alertTradePlansByWatchlistName[watchlistName]
    ).filter((id) => id !== 'groupNumber');
    removeTradePlanByIds(alertIdArray);
    setTimeout(() => {
      delete this.alertTradePlansByWatchlistName[watchlistName];
    }, 1000);
  };

  setWatchlistGroupNumber = (watchlistName, newGroupNumber) => {
    if (this.alertTradePlansByWatchlistName[watchlistName]) {
      this.alertTradePlansByWatchlistName[watchlistName].groupNumber =
        newGroupNumber;
    }
  };
  setDataSource = (string) => {
    this.dataSource = string;
  };
}

const AlertStoreContext = createContext(null);
export const useAlertsStore = () => useContext(AlertStoreContext);

export const AlertStoreProvider = ({ store, children }) => {
  return (
    <AlertStoreContext.Provider value={store}>
      {children}
    </AlertStoreContext.Provider>
  );
};

AlertStoreProvider.propTypes = {
  children: PropTypes.any,
  store: PropTypes.any,
};
